
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Target, Flag, CircleDot, TrendingUp, Award, Gauge } from "lucide-react";
import { ExternalRoundStats } from "@/hooks/useCourseStats";

interface PerformanceSummaryCardsProps {
  stats: ExternalRoundStats[];
}

const PerformanceSummaryCards = ({ stats }: PerformanceSummaryCardsProps) => {
  const { t } = useTranslation('course-stats');

  // Calculate aggregated statistics
  const roundsPlayed = stats.length;
  
  const avgScoreVsPar = stats.length > 0
    ? stats.reduce((sum, s) => sum + (s.score_vs_par || 0), 0) / stats.length
    : 0;

  const avgFairwayPercentage = stats.length > 0
    ? stats.reduce((sum, s) => sum + (s.fairway_percentage || 0), 0) / stats.length
    : 0;

  const avgPuttsPerHole = stats.length > 0
    ? stats.reduce((sum, s) => sum + (s.putts_per_hole || 0), 0) / stats.length
    : 0;

  const avgTotalPutts = stats.length > 0
    ? stats.reduce((sum, s) => sum + (s.total_putts || 0), 0) / stats.length
    : 0;

  const avgGirPercentage = stats.length > 0
    ? stats.reduce((sum, s) => sum + (s.gir_percentage || 0), 0) / stats.length
    : 0;

  // Best 8 rounds differential
  const sortedByScore = [...stats]
    .filter(s => s.score_vs_par !== null)
    .sort((a, b) => (a.score_vs_par || 0) - (b.score_vs_par || 0));
  const best8 = sortedByScore.slice(0, 8);
  const avgBest8 = best8.length > 0
    ? best8.reduce((sum, s) => sum + (s.score_vs_par || 0), 0) / best8.length
    : 0;

  const kpis = [
    {
      icon: <Flag className="h-5 w-5 text-golf-600" />,
      label: t('kpis.roundsPlayed'),
      value: roundsPlayed.toString(),
    },
    {
      icon: <TrendingUp className="h-5 w-5 text-blue-600" />,
      label: t('kpis.avgScore'),
      value: avgScoreVsPar >= 0 ? `+${avgScoreVsPar.toFixed(1)}` : avgScoreVsPar.toFixed(1),
      subtext: t('kpis.vsPar'),
    },
    {
      icon: <Target className="h-5 w-5 text-green-600" />,
      label: t('kpis.fairways'),
      value: `${avgFairwayPercentage.toFixed(1)}%`,
    },
    {
      icon: <CircleDot className="h-5 w-5 text-purple-600" />,
      label: t('kpis.puttsPerHole'),
      value: avgPuttsPerHole.toFixed(2),
      subtext: avgTotalPutts > 0 ? `${t('kpis.avgTotalPutts')}: ${avgTotalPutts.toFixed(2)}` : undefined,
    },
    {
      icon: <Gauge className="h-5 w-5 text-orange-600" />,
      label: t('kpis.gir'),
      value: `${avgGirPercentage.toFixed(1)}%`,
    },
    {
      icon: <Award className="h-5 w-5 text-yellow-600" />,
      label: t('kpis.best8'),
      value: avgBest8 >= 0 ? `+${avgBest8.toFixed(1)}` : avgBest8.toFixed(1),
      subtext: best8.length < 8 ? `(${best8.length} ${t('kpis.rounds')})` : undefined,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {kpis.map((kpi, index) => (
        <Card key={index} className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              {kpi.icon}
              <span className="text-sm text-muted-foreground">{kpi.label}</span>
            </div>
            <div className="text-2xl font-bold text-golf-800">{kpi.value}</div>
            {kpi.subtext && (
              <div className="text-xs text-muted-foreground">{kpi.subtext}</div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default PerformanceSummaryCards;
